import { ICompileService, isArray } from "angular";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../records/grids/columns/columnDefinitionService";
import {
  ColumnDefinitionSetService,
  ColumnDefinitionSetServiceProvider,
  vsProviderName as ColumnDefinitionSetServiceProviderName,
  vsServiceName as ColumnDefinitionSetServiceName,
} from "../../../records/grids/columns/columnDefinitionSetService";
import {
  RecordGridViewService,
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
  vsServiceName as RecordGridViewServiceName,
} from "../../../records/views/recordGridView";
import { RecordDataSource } from "../../../records/grids";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
} from "../../../records/grids/settings/gridSettingsService";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";
import { FilterProvider } from "../../../records/grids/columns/filters/FilterProvider";

import { OptionsFilter } from "../../../records/grids/columns/filters/subFilters/OptionsFilter";

import { machineConfigurationObjectName } from "../../shared/names";
export { machineConfigurationObjectName as vsObjectName } from "../../shared/names";

import { vsServiceName as MachineDetailTabsServiceName } from "../../shared/components/views/machines/detail/tabs";

import { vsObjectName as machineConfigurationTypeName } from "./machineConfigurationType";
import {
  FormViewInstanceConfig,
  FormViewService,
  FormViewServiceProvider,
  vsProviderName as FormViewServiceProviderName,
  vsServiceName as FormViewServiceName,
} from "../../../records/forms/formView";
import { Machine } from "../models/machine";
import { ServiceResponse } from "../../../records/server/query";
import {
  AuthorizedUiElementConfig,
  AuthorizedUiElementService,
} from "../../../ui/authorizedUiElement";
import {
  IExpressionFunction,
  IFieldArray,
  IFieldRuntimeObject,
  ITemplateScope,
  IWatcher,
} from "AngularFormly";
import fieldHelpers, { columnLayout } from "../../../ui-formly/fieldHelpers";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../records/forms/formly/fieldConfigurationHelpers";
import { get, set } from "lodash";
import { LookupManagementService } from "../../../records/lookup/lookupManagement";
import { vsLookupManagementServiceName as MachineLookupManagementServiceName } from "../../shared/lookups";
import * as lookupNames from "../configs/lookup";
import { vsObjectName as epromObjectName } from "../configs/machineEpromConfigurations";
import { MachineConfigurationTypeView } from "../models/machineConfigurationType";
import {
  SobobaUtilsService,
  vsServiceName as SobobaUtilsServiceName,
} from "../utils";

export const machineConfigViewDefaultSort =
  "PropertyName,Status,Location,SerialNumber,-Date,Order";

machineConfigurationConfig.$inject = [
  RecordGridViewServiceProviderName,
  ColumnDefinitionSetServiceProviderName,
  FormViewServiceProviderName,
];

export interface MachineEditFormViewConfig extends FormViewInstanceConfig {
  machineFieldsEditable?: boolean;
}

export function machineConfigurationConfig(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider,
  formViewServiceProvider: FormViewServiceProvider
) {
  registerGrid(
    recordGridViewServiceProvider,
    columnDefinitionSetServiceProvider
  );
  registerForm(formViewServiceProvider);
}

function registerGrid(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider
) {
  recordGridViewServiceProvider.addGridConfiguration(
    machineConfigurationObjectName,
    [
      ServerUtilityName,
      GridSettingsServiceName,
      (
        serverUtility: ServerUtility,
        gridSettingsService: GridSettingsService
      ) => {
        return {
          columnDefinitionSet: machineConfigurationObjectName,
          defaultSort: machineConfigViewDefaultSort,
          gridDataSource: new RecordDataSource(
            serverUtility,
            "/api/machines/MachineConfiguration"
          ),
          gridSettings: gridSettingsService.getGridSettings(
            Symbol(machineConfigurationObjectName)
          ),
          nameSingular: "Machine Configuration",
          namePlural: "Machines Configurations",
          showAddButton: false,
          showAddButtonPermission: "machineEdit",
        };
      },
    ]
  );

  columnDefinitionSetServiceProvider.addColumnDefinitionSet(
    machineConfigurationObjectName,
    [
      ColumnDefinitionServiceName,
      ColumnDefinitionSetServiceName,
      "$compile",
      (
        columnDefinitionService: ColumnDefinitionService,
        columnDefinitionSetService: ColumnDefinitionSetService,
        $compile: ICompileService
      ) => {
        const machineConfigurationTypeColumns =
          columnDefinitionSetService.getColumnDefinitionSet(
            machineConfigurationTypeName
          );

        const columns = [
          columnDefinitionService.getNumberColDef({
            field: "Id",
            hide: true,
          }),
          columnDefinitionService.getNumberColDef({
            field: "MachineId",
            hide: true,
          }),
          columnDefinitionService.getNumberColDef({
            field: "MachineConfigurationId",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "PropertyName",
          }),
          columnDefinitionService.getDateColDef({
            field: "Date",
          }),
          columnDefinitionService.getStringColDef({
            field: "Location",
          }),
          columnDefinitionService.getStringColDef({
            field: "Section",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "Bank",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "Position",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "WarehouseLocation",
            header: "Warehouse Location",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "SerialNumber",
            header: "Serial Number",
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineManufacturerName",
            header: "Manufacturer",
          }),
          columnDefinitionService.getStringColDef({
            field: "Notes",
          }),
          columnDefinitionService.getBooleanColDef({
            field: "CurrentConfiguration",
            header: "Current Configuration",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "ChangeType",
            filter: new FilterProvider("ChangeType", $compile)
              .addFilter(
                "=",
                new OptionsFilter({
                  displayName: "=",
                  queryFieldNames: ["ChangeType"],
                  options: [
                    {
                      display: "Conversion",
                      value: "Conversion",
                    },
                    {
                      display: "Decommission",
                      value: "Decommission",
                    },
                    {
                      display: "Move",
                      value: "Move",
                    },
                    {
                      display: "New Machine",
                      value: "NewMachine",
                    },
                    {
                      display: "Warehouse",
                      value: "Warehouse",
                    },
                  ],
                })
              )
              .getGridFilterFactory(),
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineConversionTypeName",
            header: "Conversion Type",
          }),
          columnDefinitionService.getStringColDef({
            field: "SlotNumber",
            header: "Machine Number",
          }),
          columnDefinitionService.getStringColDef({
            field: "Status",
            filter: new FilterProvider("Status", $compile)
              .addFilter(
                "=",
                new OptionsFilter({
                  displayName: "=",
                  queryFieldNames: ["Status"],
                  options: [
                    {
                      display: "Floor",
                      value: "Floor",
                    },
                    {
                      display: "Warehoused",
                      value: "Warehoused",
                    },
                    {
                      display: "Decommissioned",
                      value: "Decommissioned",
                    },
                  ],
                })
              )
              .getGridFilterFactory(),
          }),
          columnDefinitionService.getDateColDef({
            field: "PurchaseDate",
            header: "Purchase Date",
          }),
          columnDefinitionService.getDateColDef({
            field: "ActiveDate",
            header: "Active Date",
          }),
          columnDefinitionService.getStringColDef({
            field: "BankConfigName",
            header: "Bank Config",
            hide: true,
          }),
          columnDefinitionService.getBooleanColDef({
            field: "CameraCoverage",
            header: "Camera Coverage",
            hide: true,
          }),
          columnDefinitionService.getBooleanColDef({
            field: "FreePlayEnabled",
            header: "Free Play Enabled",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineCabinetTypeName",
            header: "Cabinet Type",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "MachineModelNumberName",
            header: "Model",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "GameTypeName",
            header: "Game Type",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "NumberOfReels",
            header: "Number of Reels",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "ButtonPanelId",
            header: "Button Panel",
            hide: true,
          }),
          columnDefinitionService.getBooleanColDef({
            field: "LightningLink",
            header: "Lightning Link",
            hide: true,
          }),
          columnDefinitionService.getBooleanColDef({
            field: "DragonLink",
            header: "Dragon Link",
            hide: true,
          }),
          columnDefinitionService.getBooleanColDef({
            field: "OneLink",
            header: "One Link",
            hide: true,
          }),
          columnDefinitionService.getBooleanColDef({
            field: "LCDTopper",
            header: "LCD Topper",
            hide: true,
          }),
          columnDefinitionService.getBooleanColDef({
            field: "VIP",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "CoinDivisionName",
            header: "Coin Division",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "ValidatorDivisionName",
            header: "Validator Division",
            hide: true,
          }),
          columnDefinitionService.getBooleanColDef({
            field: "ProgressiveController",
            header: "Progressive Controller",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "ControllerLocation",
            header: "Controller Location",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "ProgressiveSerial",
            header: "Progressive Serial",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "ControllerType",
            header: "Controller Type",
            hide: true,
          }),
          columnDefinitionService.getBooleanColDef({
            field: "LeaseMachine",
            header: "Lease Machine",
            hide: true,
          }),
          columnDefinitionService.getBooleanColDef({
            field: "LeasedSoftware",
            header: "Leased Software",
            hide: true,
          }),
          columnDefinitionService.getStringColDef({
            field: "SpecialtyPUPD",
            header: "Specialty PUPD",
            hide: true,
          }),

          columnDefinitionService.getStringColDef({
            field: "LeaseTypeName",
            header: "Lease Type",
            hide: true,
          }),
          columnDefinitionService.getNumberColDef({
            field: "LeaseFactor",
            header: "Lease Factor",
            hide: true,
          }),
          columnDefinitionService.getNumberColDef({
            field: "AdditionalDailyLease",
            header: "Additional Daily Lease",
            hide: true,
          }),
          ...machineConfigurationTypeColumns,
        ];

        return columns;
      },
    ]
  );
}

function registerForm(formViewServiceProvider: FormViewServiceProvider) {
  formViewServiceProvider.addFormViewConfig(machineConfigurationObjectName, [
    "config",
    FormViewServiceName,
    MachineDetailTabsServiceName,
    FieldConfigurationHelperServiceName,
    MachineLookupManagementServiceName,
    RecordGridViewServiceName,
    SobobaUtilsServiceName,
    (
      config: MachineEditFormViewConfig,
      formViewService: FormViewService,
      machineDetailTabsService: AuthorizedUiElementService<AuthorizedUiElementConfig>,
      fieldConfigurationHelperService: FieldConfigurationHelperService,
      machineLookupManagementService: LookupManagementService,
      recordGridViewService: RecordGridViewService,
      sobobaUtilsService: SobobaUtilsService
    ) => {
      const epromGridConfiguration =
        recordGridViewService.getGridConfiguration(epromObjectName)!;

      return {
        fields: <IFieldArray>[
          ...fieldHelpers.columnLayout(
            [
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: getConfigurationFieldKey("MachineConversionTypeId"),
                  objectName: lookupNames.conversionTypeName,
                  hideExpression: hideIfNotChangeType("Conversion"),
                }
              ),
            ],
            2
          ),
          fieldHelpers.headerField({
            headerText: "Date/Location",
            key: "DateLocation",
          }),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleDateField({
                key: getConfigurationFieldKey("Date"),
                label: "Date",
                required: true,
              }),
              fieldHelpers.simpleDateField({
                key: getConfigurationFieldKey("PurchaseDate"),
                label: "Purchase Date",
              }),
              fieldHelpers.simpleDateField({
                key: getConfigurationFieldKey("ActiveDate"),
                label: "Active Date",
              }),
              fieldConfigurationHelperService.selectAsyncField({
                queryUrl: "/api/properties",
                getDefaultItem: (model: any) =>
                  get(model, getConfigurationFieldKey("Property")),
                label: "Property",
                key: getConfigurationFieldKey("PropertyId"),
                required: true,
              }),
            ],
            4
          ),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("Section"),
                label: "Section",
                expressionProperties: requireIfStatus("Floor"),
                hideExpression: hideIfNotStatus("Floor"),
                watcher: watchAndSetUndefinedIfNotStatus(
                  "Floor",
                  getConfigurationFieldKey("Section")
                ),
              }),
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("Bank"),
                label: "Bank",
                expressionProperties: requireIfStatus("Floor"),
                hideExpression: hideIfNotStatus("Floor"),
                watcher: watchAndSetUndefinedIfNotStatus(
                  "Floor",
                  getConfigurationFieldKey("Bank")
                ),
              }),
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("Position"),
                label: "Position",
                expressionProperties: requireIfStatus("Floor"),
                hideExpression: hideIfNotStatus("Floor"),
                watcher: watchAndSetUndefinedIfNotStatus(
                  "Floor",
                  getConfigurationFieldKey("Position")
                ),
              }),
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: getConfigurationFieldKey("BankConfigId"),
                  objectName: lookupNames.bankConfigName,
                  hideExpression: hideIfNotStatus("Floor"),
                  watcher: watchAndSetUndefinedIfNotStatus(
                    "Floor",
                    getConfigurationFieldKey("BankConfigId")
                  ),
                }
              ),
            ],
            4
          ),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("WarehouseLocation"),
                label: "Warehouse Location",
                expressionProperties: requireIfStatus("Warehoused"),
                hideExpression: hideIfNotStatus("Warehoused"),
                watcher: watchAndSetUndefinedIfNotStatus(
                  "Warehoused",
                  getConfigurationFieldKey("WarehouseLocation")
                ),
              }),
            ],
            4
          ),
          fieldHelpers.headerField({
            headerText: "EGM Information",
            key: "EGMInformation",
          }),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleStringField({
                key: getMachineFieldKey("SerialNumber"),
                readonly: !config.machineFieldsEditable,
                label: "Serial Number",
                required: true,
              }),
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("SlotNumber"),
                label: "Machine Number",
                expressionProperties: requireIfStatus("Floor"),
              }),

              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: getMachineFieldKey("MachineManufacturerId"),
                  label: "Manufacturer",
                  required: true,
                  objectName: lookupNames.manufacturerName,
                  readonly: !config.machineFieldsEditable,
                }
              ),
              {
                key: getConfigurationFieldKey("Status"),
                name: "Status",
                type: "select",
                templateOptions: {
                  label: "Status",
                  options: [
                    {
                      label: "Floor",
                      value: "Floor",
                    },
                    {
                      label: "Warehoused",
                      value: "Warehoused",
                    },
                    {
                      label: "Decommissioned",
                      value: "Decommissioned",
                    },
                  ],
                  required: true,
                  labelProp: "label",
                  valueProp: "value",
                },
              },
            ],
            4
          ),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleBoolField({
                key: getConfigurationFieldKey("CameraCoverage"),
                label: "Camera Coverage",
                defaultValue: false,
              }),
              fieldHelpers.simpleBoolField({
                key: getConfigurationFieldKey("FreePlayEnabled"),
                label: "Free Play Enabled",
                defaultValue: false,
              }),
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: getMachineFieldKey("MachineCabinetTypeId"),
                  objectName: lookupNames.cabinetTypeName,
                  readonly: !config.machineFieldsEditable,
                  required: config.machineFieldsEditable,
                }
              ),
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: getMachineFieldKey("MachineModelNumberId"),
                  objectName: lookupNames.modelNumberName,
                  readonly: !config.machineFieldsEditable,
                  required: config.machineFieldsEditable,
                }
              ),
            ],
            4
          ),
          ...fieldHelpers.columnLayout(
            [
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: getMachineFieldKey("GameTypeId"),
                  objectName: lookupNames.gameTypeName,
                  readonly: !config.machineFieldsEditable,
                  required: config.machineFieldsEditable,
                }
              ),
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("NumberOfReels"),
                label: "Number of Reels",
              }),
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: getConfigurationFieldKey("ButtonPanelId"),
                  objectName: lookupNames.buttonPanelName,
                }
              ),
            ],
            4
          ),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleBoolField({
                key: getConfigurationFieldKey("LightningLink"),
                label: "Lightning Link",
                defaultValue: false,
              }),
              fieldHelpers.simpleBoolField({
                key: getConfigurationFieldKey("DragonLink"),
                label: "Dragon Link",
                defaultValue: false,
              }),
              fieldHelpers.simpleBoolField({
                key: getConfigurationFieldKey("OneLink"),
                label: "One Link",
                defaultValue: false,
              }),
            ],
            4
          ),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleBoolField({
                key: getConfigurationFieldKey("LCDTopper"),
                label: "LCD Topper",
                defaultValue: false,
              }),
              fieldHelpers.simpleBoolField({
                key: getConfigurationFieldKey("VIP"),
                label: "VIP",
                defaultValue: false,
              }),
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: getConfigurationFieldKey("CoinDivisionId"),
                  objectName: lookupNames.coinDivisionName,
                }
              ),
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: getConfigurationFieldKey("ValidatorDivisionId"),
                  objectName: lookupNames.validatorDivisionName,
                }
              ),
            ],
            4
          ),
          fieldHelpers.headerField({
            headerText: "Game Configurations",
            key: "GameConfigurationsHeader",
          }),
          ...fieldHelpers.columnLayout(
            [
              fieldConfigurationHelperService.selectAsyncField({
                key: getConfigurationFieldKey("MachineConfigurationTypeId"),
                label: "Configuration Type",
                queryUrl: "/api/machines/MachineConfigurationType",
                serverItemMapper: (model: MachineConfigurationTypeView) => {
                  return {
                    Id: model.Id,
                    Name: model.MachineConfigurationTypeName!,
                  };
                },
                queryBuilder: (search: string) => {
                  return {
                    MachineConfigurationTypeNameContains: search,
                  };
                },
                getDefaultItem: (model: any) =>
                  get(
                    model,
                    getConfigurationFieldKey("MachineConfigurationType")
                  ),
                expressionProperties: requireIfStatus("Floor"),
              }),
            ],
            4
          ),
          fieldHelpers.headerField({
            headerText: "Progressive Information",
            key: "ProgressiveInformationHeader",
          }),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleBoolField({
                key: getConfigurationFieldKey("ProgressiveController"),
                label: "Progressive Controller",
                defaultValue: false,
              }),
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("ControllerLocation"),
                label: "Controller Location",
              }),
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("ProgressiveSerial"),
                label: "Progressive Serial",
              }),
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("ControllerType"),
                label: "Controller Type",
              }),
            ],
            4
          ),
          fieldHelpers.headerField({
            headerText: "Lease Information",
            key: "LeaseInformationHeader",
          }),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleBoolField({
                key: getConfigurationFieldKey("LeaseMachine"),
                label: "Lease Machine",
                defaultValue: false,
              }),
              fieldHelpers.simpleBoolField({
                key: getConfigurationFieldKey("LeasedSoftware"),
                label: "Lease Software",
                defaultValue: false,
              }),
              fieldHelpers.simpleStringField({
                key: getConfigurationFieldKey("SpecialtyPUPD"),
                label: "Specialty PUPD",
              }),
            ],
            4
          ),
          ...fieldHelpers.columnLayout(
            [
              fieldConfigurationHelperService.lookupField(
                machineLookupManagementService,
                {
                  key: getConfigurationFieldKey("LeaseTypeId"),
                  objectName: lookupNames.leaseTypeName,
                }
              ),
              fieldHelpers.simpleNumberField({
                key: getConfigurationFieldKey("LeaseFactor"),
                label: "Lease Factor",
              }),
              fieldHelpers.simpleNumberField({
                key: getConfigurationFieldKey("AdditionalDailyLease"),
                label: "Additional Daily Lease",
              }),
            ],
            4
          ),

          fieldHelpers.headerField({
            headerText: "Eproms",
            key: "EpromHeader",
          }),
          fieldConfigurationHelperService.repeaterField({
            key: getConfigurationFieldKey("Eproms"),
            allowAdd: true,
            allowAddBottom: true,
            displayNameSingular: epromGridConfiguration.nameSingular ?? "Eprom",
            displayNamePlural: epromGridConfiguration.namePlural ?? "Eproms",
            repeaterFields: fieldHelpers.columnLayout(
              [
                fieldHelpers.simpleStringField({
                  key: "EpromLabel",
                  label: "Label",
                  required: true,
                  expressionProperties: {
                    "templateOptions.readonly": (
                      _: any,
                      __: any,
                      scope: ITemplateScope
                    ) => sobobaUtilsService.isDefaultEprom(<any>scope.model),
                  },
                }),
                fieldHelpers.simpleStringField({
                  key: "EpromVersion",
                  label: "Version",
                }),
                {
                  key: "copyDelete",
                  type: "recordRepeaterCopyDelete",
                  className: "col-xs-6",
                  templateOptions: {
                    label: " ",
                    allowMove: true,
                  },
                  hideExpression: (_, __, scope): any => {
                    return sobobaUtilsService.isDefaultEprom(<any>scope.model);
                  },
                },
              ],
              4
            ),
            showHeader: false,
          }),
          fieldHelpers.headerField({
            headerText: "Files",
            key: "FileHeader",
          }),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleSingleFileUpload({
                key: getConfigurationFieldKey("SlotServiceRequestForm"),
                label: "Slot Service Request Form",
              }),
              fieldHelpers.simpleSingleFileUpload({
                key: getConfigurationFieldKey("TheoreticalHoldWorksheet"),
                label: "Theoretical Hold Worksheet",
              }),
              fieldHelpers.simpleSingleFileUpload({
                key: getConfigurationFieldKey("ShipmentNotification"),
                label: "Shipment Notification",
              }),
              fieldHelpers.simpleSingleFileUpload({
                key: getConfigurationFieldKey("IntConfigSheet"),
                label: "Int Config Sheet",
              }),
            ],
            4
          ),
          {
            key: getConfigurationFieldKey("Files"),
            type: "recordFileUpload",
            templateOptions: {
              label: "Files",
              allowMultiple: true,
              hideLabel: true,
            },
          },
          fieldHelpers.headerField({
            headerText: "Notes",
            key: "NotesHeader",
          }),
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.textAreaField({
                key: getConfigurationFieldKey("Notes"),
                label: " ",
                rows: 5,
              }),
            ],
            1
          ),
        ],
        name: machineConfigurationObjectName,
        submitForm: (args) =>
          formViewService
            .submitForm<ServiceResponse<Machine>>({
              formObject: args.model,
              formViewController: args.formViewController,
              method: "POST",
              url: "/api/machines/Machine",
            })
            .then((result) =>
              machineDetailTabsService.navigateToChildByIndex(0, {
                machineId: result.Result.Id,
              })
            ),
        title: () => "Machine",
        showButtonsAtTop: true,
      };
    },
  ]);
}

function requireIfStatus(status: string): {
  [key: string]: IExpressionFunction;
} {
  return {
    "templateOptions.required": (_, __, scope) =>
      scope.model?.Configuration.Status === status,
  };
}

function hideIfNotStatus(status: string) {
  return (_: any, __: any, scope: ITemplateScope) =>
    get(scope.model, getConfigurationFieldKey("Status")) !== status;
}

function hideIfNotChangeType(ChangeType: string) {
  return (_: any, __: any, scope: ITemplateScope) =>
    get(scope.model, getConfigurationFieldKey("ChangeType")) !== ChangeType;
}

function watchAndSetUndefinedIfNotStatus(
  status: string,
  field: string | string[]
): IWatcher {
  return {
    expression: (_, scope) => scope.model?.Configuration.Status !== status,
    listener: blankFieldListener(field),
  };
}

function blankFieldListener(
  field: string | string[]
): (
  field: IFieldRuntimeObject,
  newValue: any,
  oldValue: any,
  scope: ITemplateScope,
  stopWatching: Function
) => void {
  const fields = isArray(field) ? <string[]>field : [<string>field];
  return (_, newValue, ___, scope) => {
    if (newValue) {
      fields.forEach((f) => set(scope.model ?? {}, f, undefined));
    }
  };
}

function getConfigurationFieldKey(fieldName: string) {
  return `Configuration.${fieldName}`;
}

function getMachineFieldKey(fieldName: string) {
  return `Machine.${fieldName}`;
}
